// Машина состояний уведомлений.
// Source: docs/specs/design-revision/a-notifications.md §2.5-2.6 (R-3, R-4, R-5).
// Контракт зафиксирован тестами в use-toast.reducer.test.ts — они написаны до кода.
//
// Что было не так в исходной shadcn-версии:
//   TOAST_LIMIT = 1 и `[toast, ...state.toasts].slice(0, 1)` — новая плашка
//   выбрасывала предыдущую из массива мгновенно, без `open: false` и без
//   анимации ухода. При пакетной загрузке это давало мелькание, а сообщение
//   исчезало, не побывав на экране.
//   TOAST_REMOVE_DELAY = 1_000_000 (~16 минут) — закрытые тосты висели
//   в состоянии до конца сессии.
//
// Теперь два массива: `visible` (не больше трёх) и `pending` (без лимита).
// Голова очереди выходит на экран только после того, как ушедший удалён, —
// то есть после анимации, а не одновременно с ней.

import * as React from 'react';

import type { ToastActionElement, ToastProps } from '@/components/ui/toast';

/** Сколько плашек видно одновременно. Очередь за ними не ограничена. */
export const TOAST_VISIBLE_LIMIT = 3;

/** Длительность анимации ухода: столько живёт закрытый тост до удаления. */
export const TOAST_EXIT_MS = 400;

export const TOAST_MS = {
  /** Нейтральное и успех — уходят сами. */
  short: 4000,
  /** Есть кнопка действия: нужно время дотянуться. */
  action: 7000,
  /**
   * Ошибка ждёт решения человека.
   * Не `Infinity`: `setTimeout` приводит его к нулю и срабатывает немедленно.
   * Берём максимум int32 — это ~24.8 суток.
   */
  persistent: 2_147_483_647,
} as const;

export type ToastVariant = 'success' | 'warning' | 'destructive' | 'info';

/**
 * `foreground` создаёт assertive live region — именно этим Radix объявляет
 * срочные сообщения. Произвольный `aria-live` на `Toast.Root` не сработал бы:
 * регионом управляет сам Radix.
 */
export type ToastAnnounce = 'foreground' | 'background';

export type ToastPolicy = { duration: number; type: ToastAnnounce };

/**
 * Единственное место, где решается, сколько живёт плашка и как объявляется.
 * Вызывающий код длительность не передаёт: иначе она разъедется по 59 местам.
 *
 * Ждут решения человека только ошибки. Предупреждение по умолчанию уходит само:
 * «Лимит файлов» или «Отправим первые 20» сообщают о том, что уже произошло, и
 * заставлять закрывать их руками незачем. Исключения — состояния, которые
 * сохраняются и после прочтения, вроде потери связи: они помечаются явным
 * `persistent` на месте вызова.
 */
export function resolveToastPolicy(
  variant: ToastVariant,
  hasAction: boolean,
  persistent = false,
): ToastPolicy {
  const waitsForHuman = variant === 'destructive' || persistent;
  return {
    duration: waitsForHuman
      ? TOAST_MS.persistent
      : hasAction
        ? TOAST_MS.action
        : TOAST_MS.short,
    type: variant === 'destructive' ? 'foreground' : 'background',
  };
}

export type ToasterToast = ToastProps & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
  variant?: ToastVariant;
  /**
   * Ключ схлопывания. Приходит от МАШИННОЙ причины отказа
   * (`polling:parser_unavailable`), а не от переведённой строки: разные отказы
   * могут одинаково называться «Ошибка», но иметь разный текст и требовать
   * разных действий. Без ключа не схлопываем вовсе.
   */
  dedupeKey?: string;
  /**
   * Не закрывать по таймеру. Для состояний, которые остаются истинными и после
   * прочтения: потеря связи не проходит оттого, что человек её увидел.
   * Ошибкам ставить не нужно — они незакрываемы по варианту.
   */
  persistent?: boolean;
  /** Сколько раз пришло это же сообщение. Показывается как «×N». */
  occurrences: number;
  duration: number;
  announce: ToastAnnounce;
  /**
   * Меняется при повторе — Toaster включает его в `key`, Radix перемонтирует
   * плашку и начинает отсчёт заново. Так «повтор перезапускает таймер»
   * остаётся проверяемым без обращения к настоящим таймерам.
   */
  timerNonce: number;
  /** Момент создания. Нужен окну пощады при смене экрана — см. NAV_GRACE_MS. */
  createdAt: number;
};

/**
 * Окно пощады при смене экрана.
 *
 * Правило T-1 гасит незакрываемые плашки при переходе, чтобы ошибка не
 * путешествовала за человеком по приложению. Но часть ошибок раздаётся ВМЕСТЕ
 * с переходом: `Processing` при провале распознавания сначала показывает
 * причину, а следующей строкой уводит на экран загрузки. Без окна пощады
 * правило съедало ровно то сообщение, которое объясняет, почему человека
 * куда-то перекинуло, — и он видел смену экрана без объяснения.
 *
 * Различить эти два случая можно по времени. Сообщение, раздаваемое кодом
 * перехода, живёт к моменту навигации миллисекунды. Сообщение, которое человек
 * успел прочитать и после этого сам куда-то ушёл, — как минимум сотни.
 */
export const NAV_GRACE_MS = 1000;

export type ToastInput = Omit<
  ToasterToast,
  | 'id'
  | 'occurrences'
  | 'duration'
  | 'announce'
  | 'timerNonce'
  | 'createdAt'
  | 'open'
  | 'onOpenChange'
> & {
  variant?: ToastVariant;
};

export type ToastState = {
  visible: ToasterToast[];
  pending: ToasterToast[];
};

type Action =
  | { type: 'ADD_TOAST'; toast: ToasterToast }
  | { type: 'UPDATE_TOAST'; toast: Partial<ToasterToast> & Pick<ToasterToast, 'id'> }
  | { type: 'DISMISS_TOAST'; toastId?: string }
  | { type: 'REMOVE_TOAST'; toastId?: string };

let count = 0;

function genId(): string {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

/** Достраивает тост до полной записи: id, политика длительности, счётчики. */
export function makeToast(input: ToastInput): ToasterToast {
  const variant: ToastVariant = input.variant ?? 'info';
  const policy = resolveToastPolicy(variant, Boolean(input.action), input.persistent);
  return {
    ...input,
    variant,
    id: genId(),
    occurrences: 1,
    duration: policy.duration,
    announce: policy.type,
    timerNonce: 0,
    createdAt: Date.now(),
    open: true,
  };
}

/** Схлопывать можно только помеченное ключом и только без кнопки действия. */
function canDedupe(t: Pick<ToasterToast, 'dedupeKey' | 'action'>): boolean {
  return Boolean(t.dedupeKey) && !t.action;
}

function bumpOccurrence(t: ToasterToast, restartTimer: boolean): ToasterToast {
  return {
    ...t,
    occurrences: t.occurrences + 1,
    // Незакрываемому перезапускать нечего: таймера у него нет.
    timerNonce: restartTimer ? t.timerNonce + 1 : t.timerNonce,
  };
}

export function reducer(state: ToastState, action: Action): ToastState {
  switch (action.type) {
    case 'ADD_TOAST': {
      const incoming = action.toast;

      if (canDedupe(incoming)) {
        const visibleIdx = state.visible.findIndex(
          (t) => canDedupe(t) && t.dedupeKey === incoming.dedupeKey,
        );
        if (visibleIdx !== -1) {
          const target = state.visible[visibleIdx]!;
          const restart = target.duration !== TOAST_MS.persistent;
          const visible = [...state.visible];
          visible[visibleIdx] = bumpOccurrence(target, restart);
          return { ...state, visible };
        }

        const pendingIdx = state.pending.findIndex(
          (t) => canDedupe(t) && t.dedupeKey === incoming.dedupeKey,
        );
        if (pendingIdx !== -1) {
          // Место в очереди сохраняется: повтор не пролезает вперёд.
          const pending = [...state.pending];
          pending[pendingIdx] = bumpOccurrence(state.pending[pendingIdx]!, false);
          return { ...state, pending };
        }
      }

      return state.visible.length < TOAST_VISIBLE_LIMIT
        ? { ...state, visible: [...state.visible, incoming] }
        : { ...state, pending: [...state.pending, incoming] };
    }

    case 'UPDATE_TOAST': {
      const patch = (list: ToasterToast[]) =>
        list.map((t) => (t.id === action.toast.id ? { ...t, ...action.toast } : t));
      return { visible: patch(state.visible), pending: patch(state.pending) };
    }

    case 'DISMISS_TOAST': {
      const { toastId } = action;
      return {
        ...state,
        visible: state.visible.map((t) =>
          toastId === undefined || t.id === toastId ? { ...t, open: false } : t,
        ),
      };
    }

    case 'REMOVE_TOAST': {
      if (action.toastId === undefined) return { visible: [], pending: [] };

      const visible = state.visible.filter((t) => t.id !== action.toastId);
      if (visible.length === state.visible.length) {
        // Ушёл тост, который ещё стоял в очереди (например, снят целиком).
        return { ...state, pending: state.pending.filter((t) => t.id !== action.toastId) };
      }

      // Место освободилось — только теперь выпускаем следующего.
      const [next, ...rest] = state.pending;
      return next
        ? { visible: [...visible, next], pending: rest }
        : { ...state, visible };
    }
  }
}

const listeners: Array<(state: ToastState) => void> = [];
let memoryState: ToastState = { visible: [], pending: [] };
const exitTimers = new Map<string, ReturnType<typeof setTimeout>>();

function dispatch(action: Action): void {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => listener(memoryState));
}

/** Пометить закрытым, а через анимацию ухода удалить и подтянуть очередь. */
function scheduleRemoval(toastId: string): void {
  if (exitTimers.has(toastId)) return;
  exitTimers.set(
    toastId,
    setTimeout(() => {
      exitTimers.delete(toastId);
      dispatch({ type: 'REMOVE_TOAST', toastId });
    }, TOAST_EXIT_MS),
  );
}

export function toast(input: ToastInput) {
  const created = makeToast(input);
  dispatch({ type: 'ADD_TOAST', toast: created });

  // При схлопывании новая запись не появилась — возвращаем id той, что на экране.
  const id =
    memoryState.visible.find((t) => t.id === created.id)?.id ??
    memoryState.pending.find((t) => t.id === created.id)?.id ??
    created.id;

  const dismiss = () => {
    dispatch({ type: 'DISMISS_TOAST', toastId: id });
    scheduleRemoval(id);
  };

  return {
    id,
    dismiss,
    update: (patch: Partial<ToasterToast>) =>
      dispatch({ type: 'UPDATE_TOAST', toast: { ...patch, id } }),
  };
}

export function useToast() {
  const [state, setState] = React.useState<ToastState>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) listeners.splice(index, 1);
    };
  }, []);

  return {
    ...state,
    /** Плашки, которые сейчас на экране. Очередь за ними рендерить не нужно. */
    toasts: state.visible,
    toast,
    dismiss: (toastId?: string) => {
      dispatch({ type: 'DISMISS_TOAST', toastId });
      if (toastId) scheduleRemoval(toastId);
      else memoryState.visible.forEach((t) => scheduleRemoval(t.id));
    },
  };
}

/**
 * Смена экрана гасит плашки, которые к ней не относятся.
 *
 * `Toaster` живёт на уровне приложения и переживает навигацию — без этого
 * правила сообщение таскалось бы за человеком по всем экранам. «Анализ удалён»
 * на экране «Маркеры» выглядит так же неуместно, как незакрываемая ошибка
 * на Профиле, поэтому правило одно на все варианты.
 *
 * Разделяет случаи окно пощады: плашку, раздаваемую самим кодом перехода,
 * переход не трогает. Так «Анализ удалён» доезжает до списка загрузок,
 * а причина отказа распознавания — до экрана загрузки. См. NAV_GRACE_MS.
 */
export function dismissStaleToasts(now: number = Date.now()): void {
  memoryState.visible
    .filter((t) => t.open && now - t.createdAt >= NAV_GRACE_MS)
    .forEach((t) => {
      dispatch({ type: 'DISMISS_TOAST', toastId: t.id });
      scheduleRemoval(t.id);
    });
}
